/**
 * TrackedLinkCard — one deck link, followed visit by visit.
 *
 * The marketing site's view beacon records everything it can about each open of
 * the deck: roughly where (city, from Vercel's edge — never an IP address), on
 * what device and browser, whether that browser has opened it before, and then
 * slide by slide how long each one was on screen, how long the reader was
 * actually engaged with it, the order they read it in, and a timeline of what
 * they did (swipes, arrows, rows opened, links clicked, text copied, tab
 * switches, going idle).
 *
 * That record lives behind the site's private report. This card reads it with
 * the admin key the operator already typed to unlock the console; the site
 * checks that key with the backend, so no report key is ever in this build.
 *
 * Add another link to TRACKED to follow it too.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { styled } from '@mui/system';
import { getDeckReport } from '../../services/adminService';
import { whenAgo } from './posterScanMath';
import { VALET, FONTS, TYPE } from '../../theme';

export const TRACKED = [
    { page: 'deck', src: 'link', url: 'https://valetnetwork.co/deck?src=link', name: 'Investor deck · hyperlink' },
];

const Card = styled(Box)({
    backgroundColor: VALET.surface,
    borderRadius: '14px',
    border: `1px solid ${VALET.border}`,
    boxShadow: VALET.shadow,
    padding: '22px 24px',
    '@media (max-width: 768px)': { padding: '16px 14px' },
});
const Title = styled(Typography)({ ...TYPE.sectionTitle, marginBottom: '4px' });
const Blurb = styled(Typography)({ ...TYPE.bodySmall, color: VALET.textSecondary });
const Label = styled(Typography)({ ...TYPE.kpiLabel, marginBottom: '6px' });
const Muted = styled(Typography)({ fontFamily: FONTS.sans, fontSize: '13px', color: VALET.textMuted });
const Strong = styled(Typography)({ fontFamily: FONTS.sans, fontSize: '14px', fontWeight: 600, color: VALET.textPrimary });

const LinkOut = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginTop: '14px',
    padding: '10px 12px',
    borderRadius: '8px',
    border: `1px solid ${VALET.border}`,
    backgroundColor: VALET.surfaceMuted,
});
const LinkText = styled(Typography)({ fontFamily: FONTS.mono, fontSize: '13px', minWidth: 0, overflowWrap: 'anywhere' });
const SmallButton = styled(Button)({
    textTransform: 'none',
    fontFamily: FONTS.sans,
    fontSize: '13px',
    fontWeight: 600,
    color: VALET.green,
    whiteSpace: 'nowrap',
    minWidth: '68px',
    padding: '4px 10px',
});

const Stats = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '12px',
    margin: '18px 0 6px',
    '@media (max-width: 768px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
});
const Stat = styled(Box)({ backgroundColor: VALET.surfaceMuted, borderRadius: '10px', padding: '12px 14px' });
const StatValue = styled(Typography)({ ...TYPE.kpiValue, fontSize: '26px' });

const BarRow = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'minmax(120px, 1.4fr) 44px minmax(60px, 2fr) 56px',
    alignItems: 'center',
    gap: '10px',
    padding: '5px 0',
    borderTop: `1px solid ${VALET.border}`,
    fontFamily: FONTS.sans,
    fontSize: '13px',
    '@media (max-width: 768px)': { gridTemplateColumns: 'minmax(96px, 1.2fr) 38px minmax(40px, 1.4fr) 48px', gap: '6px' },
});
const Bar = styled('span')({ display: 'block', height: '8px', borderRadius: '4px', backgroundColor: VALET.green, minWidth: '2px' });

const Visit = styled(Box)({ borderTop: `1px solid ${VALET.border}`, padding: '12px 0' });
const VisitHead = styled(Box)({
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    cursor: 'pointer',
    '@media (max-width: 768px)': { flexDirection: 'column', gap: '4px' },
});
const Live = styled('span')({
    fontFamily: FONTS.sans,
    fontSize: '11px',
    fontWeight: 700,
    color: VALET.surface,
    backgroundColor: VALET.green,
    borderRadius: '4px',
    padding: '1px 6px',
    marginLeft: '8px',
});
const Chip = styled('span')({
    display: 'inline-block',
    fontFamily: FONTS.sans,
    fontSize: '12px',
    backgroundColor: VALET.greenLight,
    borderRadius: '6px',
    padding: '2px 7px',
    margin: '3px 4px 3px 0',
    whiteSpace: 'nowrap',
});
const Line = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '56px 1fr',
    gap: '8px',
    padding: '3px 0',
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textSecondary,
    overflowWrap: 'anywhere',
});

/** "Mon, Sep 14 · 10:32 AM" in New York. */
const stamp = (iso) => {
    if (!iso) return 'unknown time';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return 'unknown time';
    return new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    }).format(d).replace(' at ', ' · ');
};

const dur = (sec) => {
    const s = Math.round(Number(sec) || 0);
    if (s <= 0) return '0s';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return s % 60 ? `${m}m ${s % 60}s` : `${m}m`;
    return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const median = (xs) => {
    if (!xs.length) return 0;
    const a = [...xs].sort((x, y) => x - y);
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};

const WORDS = {
    hide: 'Left the tab',
    show: 'Came back to the tab',
    blur: 'Window lost focus',
    focus: 'Window focused',
    idle: 'Went idle on slide',
    back: 'Active again on slide',
    link: 'Clicked link',
    click: 'Clicked',
    copy: 'Copied',
    zoom: 'Pinch-zoomed to',
    print: 'Printed',
    resize: 'Resized window to',
    rotate: 'Rotated to',
    fullscreen: 'Fullscreen',
    shortcut: 'Shortcut',
    reload: 'Reloaded the page',
    reopen: 'Came back to the page on slide',
    trimmed: 'Older events trimmed',
};

const eventText = (what, detail, titles) => {
    if (what === 'slide') {
        const [n, via] = String(detail).split(' · ');
        const t = titles[Number(n) - 1];
        return `Slide ${n}${t ? ` · ${t}` : ''}${via ? ` (${via})` : ''}`;
    }
    if (what === 'close' && detail) return `Closed the page on slide ${detail}`;
    if (what === 'open') return `Opened “${detail}”`;
    if (what === 'close') return `Closed “${detail}”`;
    return `${WORDS[what] || what}${detail ? ` ${detail}` : ''}`;
};

const where = (v) => [v.city, v.region, v.country].filter(Boolean).join(', ') || 'Location unknown';
const device = (v) => [v.device, v.os, v.browser].filter(Boolean).join(' · ');
const from = (v) => {
    if (!v.ref) return v.inApp ? `inside ${v.inApp}` : 'direct';
    try {
        return new URL(v.ref).hostname + (v.inApp ? ` · inside ${v.inApp}` : '');
    } catch {
        return v.ref;
    }
};

function VisitRow({ v, titles }) {
    const [open, setOpen] = useState(false);
    const t = v.titles && v.titles.length ? v.titles : titles;
    const live = !v.ended && v.updatedAt && Date.now() - new Date(v.updatedAt).getTime() < 60000;
    const rows = Object.entries(v.perSlide || {})
        .map(([n, s]) => ({ n: Number(n), s }))
        .sort((a, b) => a.n - b.n);
    const maxS = Math.max(1, ...rows.map((r) => r.s[0] || 0));
    return (
        <Visit>
            <VisitHead onClick={() => setOpen((o) => !o)} role="button" aria-expanded={open}>
                <Box sx={{ minWidth: 0 }}>
                    <Strong component="span">{stamp(v.at)}</Strong>
                    <Muted component="span"> · {whenAgo(v.at)}</Muted>
                    {live ? <Live>reading now</Live> : null}
                    <Muted sx={{ mt: '2px' }}>
                        {where(v)} · {device(v) || 'device unknown'} · from {from(v)}
                        {v.visitN > 1 ? ` · visit #${v.visitN} from this browser` : ''}
                        {v.bot ? ' · looks automated' : ''}
                    </Muted>
                </Box>
                <Muted sx={{ whiteSpace: 'nowrap' }}>
                    {v.maxSlide ? `slide ${v.maxSlide} of ${v.slides || '?'}` : 'no depth yet'} · {dur(v.active || v.seconds)} engaged
                    {' '}{open ? '▴' : '▾'}
                </Muted>
            </VisitHead>
            {open ? (
                <Box sx={{ mt: '10px' }}>
                    {rows.length ? (
                        <Box>
                            <BarRow sx={{ borderTop: 'none' }}>
                                <Label sx={{ mb: 0 }}>Slide</Label>
                                <Label sx={{ mb: 0 }}>Seen</Label>
                                <span />
                                <Label sx={{ mb: 0 }}>Engaged</Label>
                            </BarRow>
                            {rows.map(({ n, s }) => (
                                <BarRow key={n}>
                                    <span>{n}. {t[n - 1] || ''}</span>
                                    <span>{s[2] > 1 ? `${s[2]}×` : ''}</span>
                                    <Bar sx={{ width: `${Math.round(((s[0] || 0) / maxS) * 100)}%` }} />
                                    <span>{dur(s[1])}</span>
                                </BarRow>
                            ))}
                        </Box>
                    ) : (
                        <Muted>No slide-by-slide record for this visit.</Muted>
                    )}
                    {v.path && v.path.length ? (
                        <Box sx={{ mt: '10px' }}>
                            <Label>Order they read it</Label>
                            {v.path.map(([n, , d], i) => (
                                <Chip key={i}>{n}{d ? ` · ${dur(d)}` : ''}</Chip>
                            ))}
                        </Box>
                    ) : null}
                    {v.events && v.events.length ? (
                        <Box sx={{ mt: '10px' }}>
                            <Label>Timeline</Label>
                            {v.events.map(([s, what, detail], i) => (
                                <Line key={i}>
                                    <span>+{dur(s)}</span>
                                    <span>{eventText(what, detail, t)}</span>
                                </Line>
                            ))}
                        </Box>
                    ) : null}
                    <Muted sx={{ mt: '10px', overflowWrap: 'anywhere' }}>
                        {[v.screen && `screen ${v.screen}`, v.viewport && `window ${v.viewport}`, v.lang, v.tz, v.localTime && `their clock ${v.localTime}`, v.conn]
                            .filter(Boolean)
                            .join(' · ')}
                    </Muted>
                </Box>
            ) : null}
        </Visit>
    );
}

export default function TrackedLinkCard({ copy, copied }) {
    const link = TRACKED[0];
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            setData(await getDeckReport({ page: link.page, src: link.src }));
        } catch (err) {
            setError(err.message || 'Could not load the visit report');
        } finally {
            setLoading(false);
        }
    }, [link.page, link.src]);

    useEffect(() => { load(); }, [load]);

    const visits = (data && data.visits) || [];
    const humans = visits.filter((v) => !v.bot);
    const titles = (humans.find((v) => v.titles && v.titles.length) || {}).titles || [];
    const browsers = new Set(humans.map((v) => v.vid).filter(Boolean)).size || humans.length;
    const reported = humans.filter((v) => v.seconds > 0 || v.maxSlide > 0);
    const slideCount = Math.max(titles.length, ...reported.map((v) => v.slides || 0), 0);
    const funnel = Array.from({ length: slideCount }, (_, i) => {
        const n = i + 1;
        const withSlide = reported.filter((v) => (v.perSlide || {})[n]);
        const reached = reported.filter((v) => v.maxSlide >= n || (v.perSlide || {})[n]).length;
        const avg = withSlide.length ? withSlide.reduce((a, v) => a + (v.perSlide[n][0] || 0), 0) / withSlide.length : 0;
        return { n, title: titles[i] || '', reached, avg };
    });
    const peak = Math.max(1, ...funnel.map((f) => f.avg));

    return (
        <Card sx={{ mb: '20px' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                <Box>
                    <Title>Tracked link</Title>
                    <Blurb>
                        {link.name}. Every open of this link, slide by slide: where, on what, how
                        long on each slide, and what they did. Tap a visit for its timeline.
                    </Blurb>
                </Box>
                <SmallButton onClick={load}>Refresh</SmallButton>
            </Box>

            <LinkOut>
                <LinkText>{link.url}</LinkText>
                <SmallButton onClick={() => copy && copy(link.url, 'tracked')}>
                    {copied === 'tracked' ? 'Copied' : 'Copy'}
                </SmallButton>
            </LinkOut>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ color: VALET.green }} />
                </Box>
            ) : error ? (
                <Muted sx={{ mt: '14px', color: VALET.danger }}>{error}</Muted>
            ) : (
                <>
                    <Stats>
                        <Stat><Label>Visits</Label><StatValue>{humans.length}</StatValue></Stat>
                        <Stat><Label>Browsers</Label><StatValue>{browsers}</StatValue></Stat>
                        <Stat><Label>Median engaged</Label><StatValue>{dur(median(reported.map((v) => v.active || v.seconds)))}</StatValue></Stat>
                        <Stat><Label>Last opened</Label><StatValue sx={{ fontSize: '20px', pt: '6px' }}>{humans[0] ? whenAgo(humans[0].at) : '—'}</StatValue></Stat>
                    </Stats>

                    {humans.length === 0 ? (
                        <Muted sx={{ mt: '8px' }}>
                            Nobody has opened this link yet. Opens show up here within seconds, and a
                            visit still in progress is marked “reading now”.
                        </Muted>
                    ) : (
                        <>
                            {reported.length ? (
                                <Box sx={{ mt: '16px' }}>
                                    <Label>Every slide, across these visits · reached · avg on screen</Label>
                                    {funnel.map((f) => (
                                        <BarRow key={f.n}>
                                            <span>{f.n}. {f.title}</span>
                                            <span>{Math.round((f.reached / reported.length) * 100)}%</span>
                                            <Bar sx={{ width: `${Math.round((f.avg / peak) * 100)}%` }} />
                                            <span>{dur(f.avg)}</span>
                                        </BarRow>
                                    ))}
                                </Box>
                            ) : null}
                            <Box sx={{ mt: '16px' }}>
                                <Label>Visits, newest first</Label>
                                {visits.map((v) => <VisitRow key={v.id} v={v} titles={titles} />)}
                            </Box>
                        </>
                    )}
                </>
            )}
        </Card>
    );
}
